/**
 * Homepage – Browse all halls with advanced search & filters
 */
import React, { useState, useEffect } from 'react';
import { hallAPI } from '../services/api';
import HallCard from '../components/HallCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import MapView from '../components/MapView';
import ImageSlideshow from '../components/ImageSlideshow';
import {
  Search,
  SlidersHorizontal,
  X,
  LayoutGrid,
  Map,
  MapPin,
  Users,
  Sparkles,
  Award,
  Tag,
  Zap,
  Mail,
  Phone,
  ArrowUpRight,
  ChevronDown,
} from 'lucide-react';

const HomePage = () => {
  const [halls, setHalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'

  // Advanced filters
  const [filters, setFilters] = useState({
    minCapacity: '',
    maxCapacity: '',
    minPrice: '',
    maxPrice: '',
    amenities: [],
    city: 'all',
  });

  const [showFilters, setShowFilters] = useState(false);

  // Amenities list
  const availableAmenities = [
    'AC', 'Parking', 'Kitchen', 'Stage', 'Sound System',
    'Projector', 'WiFi', 'Catering', 'Decoration', 'Green Room'
  ];

  useEffect(() => {
    fetchHalls();
  }, []);

  const fetchHalls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await hallAPI.getAll();
      const hallsData = response.data.results || response.data;
      setHalls(Array.isArray(hallsData) ? hallsData : []);
    } catch (err) {
      console.error('Error fetching halls:', err);
      setError('Failed to load halls. Please try again.');
      setHalls([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters
  const getFilteredHalls = () => {
    let filtered = halls;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((hall) =>
        hall.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        hall.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (hall.description && hall.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (hall.address && hall.address.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Capacity filter
    if (filters.minCapacity) {
      filtered = filtered.filter(hall => hall.capacity >= parseInt(filters.minCapacity));
    }
    if (filters.maxCapacity) {
      filtered = filtered.filter(hall => hall.capacity <= parseInt(filters.maxCapacity));
    }

    // Price filter
    if (filters.minPrice) {
      filtered = filtered.filter(hall => parseFloat(hall.celebration_price) >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(hall => parseFloat(hall.celebration_price) <= parseFloat(filters.maxPrice));
    }

    // City filter
    if (filters.city !== 'all') {
      filtered = filtered.filter(hall => hall.city === filters.city);
    }

    // Amenities filter
    if (filters.amenities.length > 0) {
      filtered = filtered.filter(hall => {
        const hallAmenities = Array.isArray(hall.amenities) ? hall.amenities : [];
        return filters.amenities.every(amenity =>
          hallAmenities.some(ha => ha.toLowerCase() === amenity.toLowerCase())
        );
      });
    }

    return filtered;
  };

  const filteredHalls = getFilteredHalls();

  const handleAmenityToggle = (amenity) => {
    setFilters(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const clearFilters = () => {
    setFilters({
      minCapacity: '',
      maxCapacity: '',
      minPrice: '',
      maxPrice: '',
      amenities: [],
      city: 'all',
    });
    setSearchTerm('');
  };

  const hasActiveFilters = () => {
    return searchTerm ||
           filters.minCapacity ||
           filters.maxCapacity ||
           filters.minPrice ||
           filters.maxPrice ||
           filters.city !== 'all' ||
           filters.amenities.length > 0;
  };

  // Get unique cities
  const cities = [...new Set(halls.map(h => h.city))];

  // Badge count: how many filter categories are active (excludes search)
  const activeFilterCount =
    (filters.city !== 'all' ? 1 : 0) +
    ((filters.minCapacity || filters.maxCapacity) ? 1 : 0) +
    ((filters.minPrice || filters.maxPrice) ? 1 : 0) +
    filters.amenities.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Loading halls..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ─────────────────── HERO ─────────────────── */}
      <section className="relative text-white overflow-hidden" style={{ minHeight: '100vh' }}>
        <ImageSlideshow interval={3000} />

        {/* Layered gradient – heavier at bottom for wave transition, solid enough at top for text */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/50 to-black/35" />

        {/* Centred content column */}
        <div
          className="relative z-10 flex flex-col items-center justify-center px-4 py-24 text-center"
          style={{ minHeight: '100vh' }}
        >
          {/* Brand pill */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-pink-300" />
            <span className="text-xs font-semibold text-white/90 tracking-widest uppercase">QuickVenue</span>
          </div>

          {/* Heading – split so "Find Your" is solid white and "Perfect Hall" gets the gradient */}
          <h1
            className="font-display text-5xl md:text-7xl font-bold leading-tight mb-3"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}
          >
            <span className="text-white block">Find Your</span>
            <span
              className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-300 via-pink-300 to-purple-200"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))' }}
            >
              Perfect Venue
            </span>
          </h1>

          {/* Accent bar */}
          <div className="h-1 w-20 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto rounded-full mb-4" />

          {/* Sub-heading */} 
          <p className="text-base md:text-xl text-white/75 font-light max-w-xl mb-10">
            Book stunning halls in Belagavi for weddings, celebrations &amp; unforgettable moments
          </p>

          {/* Search row */}
          <div className="w-full max-w-2xl">
            <div className="flex gap-2.5">
              {/* Search input */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Hall name, city, or features…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-800 text-base focus:outline-none focus:ring-4 focus:ring-purple-300/50 bg-white shadow-lg"
                />
              </div>

              {/* Filter toggle – clearly labelled, badge shows active count */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-5 py-4 rounded-xl font-semibold text-sm shadow-lg transition-all duration-300 whitespace-nowrap ${
                  showFilters
                    ? 'bg-purple-700 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <SlidersHorizontal className="w-5 h-5" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 bg-pink-500 text-white text-xs font-bold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Trust badges – lucide icons replace emoji */}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {[
              { icon: Award, label: 'Verified Venues' },
              { icon: Tag,   label: 'Best Prices' },
              { icon: Zap,   label: 'Instant Booking' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <Icon className="w-4 h-4 text-pink-300" />
                <span className="text-sm font-medium text-white/90">{label}</span>
              </div>
            ))}
          </div>

          {/* Scroll hint */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-6 h-6 text-white/50" />
          </div>
        </div>

        {/* Wave separator – transitions hero into page body */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full block">
            <path d="M0,100 L0,60 C240,20 480,80 720,50 C960,20 1200,70 1440,45 L1440,100 Z" fill="#f9fafb" />
          </svg>
        </div>
      </section>

      {/* ──────────── FILTER PANEL (animated slide-down) ──────────── */}
      <div
        className="overflow-hidden bg-white"
        style={{
          maxHeight: showFilters ? '800px' : '0',
          transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="border-b border-gray-100 shadow-sm">
          <div className="container mx-auto px-4 py-6 max-w-6xl">
            {/* Header row */}
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xs font-semibold text-gray-500 flex items-center gap-2 uppercase tracking-wider">
                <SlidersHorizontal className="w-4 h-4 text-purple-600" />
                Advanced Filters
              </h3>
              {hasActiveFilters() && (
                <button onClick={clearFilters} className="text-sm text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1.5 transition">
                  <X className="w-3.5 h-3.5" />
                  Clear All
                </button>
              )}
            </div>

            {/* Inputs grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* City */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">City</label>
                <select
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50 transition appearance-none cursor-pointer"
                >
                  <option value="all">All Cities</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              {/* Min Capacity */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Min Capacity</label>
                <input
                  type="number"
                  placeholder="e.g. 100"
                  value={filters.minCapacity}
                  onChange={(e) => setFilters({ ...filters, minCapacity: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50 transition"
                />
              </div>

              {/* Max Capacity */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Max Capacity</label>
                <input
                  type="number"
                  placeholder="e.g. 500"
                  value={filters.maxCapacity}
                  onChange={(e) => setFilters({ ...filters, maxCapacity: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50 transition"
                />
              </div>

              {/* Budget (min + max side by side) */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Budget (₹)</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                    className="w-1/2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50 transition"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                    className="w-1/2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50 transition"
                  />
                </div>
              </div>
            </div>

            {/* Amenity pills */}
            <div className="mt-5">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Amenities</label>
              <div className="flex flex-wrap gap-2">
                {availableAmenities.map(amenity => (
                  <button
                    key={amenity}
                    onClick={() => handleAmenityToggle(amenity)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 border ${
                      filters.amenities.includes(amenity)
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-transparent shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300 hover:text-purple-700'
                    }`}
                  >
                    {amenity}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ──────────── MAIN CONTENT ──────────── */}
      <main className="container mx-auto px-4 py-10 max-w-6xl">
        {error ? (
          <ErrorMessage message={error} onRetry={fetchHalls} />
        ) : (
          <>
            {/* Results header + view toggle */}
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {hasActiveFilters() ? 'Search Results' : 'Available Halls'}
                </h2>
                <p className="text-gray-400 text-sm mt-0.5">
                  <span className="font-semibold text-gray-600">{filteredHalls.length}</span>{' '}
                  {filteredHalls.length === 1 ? 'hall' : 'halls'} found
                  {hasActiveFilters() && (
                    <button onClick={clearFilters} className="ml-3 text-purple-600 hover:text-purple-800 font-medium transition">
                      Clear filters
                    </button>
                  )}
                </p>
              </div>

              {/* Grid / Map toggle */}
              <div className="flex bg-gray-100 p-1 rounded-lg self-start sm:self-auto">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${
                    viewMode === 'grid' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  Grid
                </button>
                <button
                  onClick={() => setViewMode('map')}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 text-sm font-medium transition-all ${
                    viewMode === 'map' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Map className="w-4 h-4" />
                  Map
                </button>
              </div>
            </div>

            {/* Active-filter tags (removable pills) */}
            {hasActiveFilters() && (
              <div className="mb-6 flex flex-wrap gap-2">
                {searchTerm && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm border border-purple-100">
                    <Search className="w-3.5 h-3.5" />
                    {searchTerm}
                    <button onClick={() => setSearchTerm('')} className="hover:text-purple-900 ml-0.5 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.city !== 'all' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm border border-blue-100">
                    <MapPin className="w-3.5 h-3.5" />
                    {filters.city}
                    <button onClick={() => setFilters({ ...filters, city: 'all' })} className="hover:text-blue-900 ml-0.5 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {(filters.minCapacity || filters.maxCapacity) && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm border border-green-100">
                    <Users className="w-3.5 h-3.5" />
                    {filters.minCapacity || '0'} – {filters.maxCapacity || '∞'}
                    <button onClick={() => setFilters({ ...filters, minCapacity: '', maxCapacity: '' })} className="hover:text-green-900 ml-0.5 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
                {filters.amenities.map(amenity => (
                  <span key={amenity} className="inline-flex items-center gap-1.5 px-3 py-1 bg-pink-50 text-pink-700 rounded-full text-sm border border-pink-100">
                    {amenity}
                    <button onClick={() => handleAmenityToggle(amenity)} className="hover:text-pink-900 ml-0.5 transition">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Grid or Map */}
            {viewMode === 'grid' ? (
              filteredHalls.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredHalls.map((hall) => (
                    <HallCard key={hall.id} hall={hall} />
                  ))}
                </div>
              ) : (
                /* Empty state */
                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-gray-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No halls found</h3>
                  <p className="text-gray-400 text-sm max-w-sm mx-auto mb-5">
                    Try adjusting your search criteria or removing some filters to discover more venues.
                  </p>
                  {hasActiveFilters() && (
                    <button
                      onClick={clearFilters}
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold text-sm shadow-sm hover:shadow-md transition-all duration-300"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              )
            ) : (
              <MapView halls={filteredHalls} />
            )}
          </>
        )}
      </main>

      {/* ──────────── FOOTER ──────────── */}
      <footer className="bg-gray-900 text-white mt-12">
        {/* Brand-gradient accent strip */}
        <div className="h-1 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600" />

        <div className="container mx-auto px-4 py-10 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* About */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-pink-400" />
                <h3 className="text-lg font-bold">QuickVenue</h3>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Your trusted platform for booking stunning halls in Belagavi for life's most special occasions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="/" className="group flex items-center gap-1.5 text-gray-300 hover:text-white text-sm transition">
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" />
                    Home
                  </a>
                </li>
                <li>
                  <a href="/admin-login" className="group flex items-center gap-1.5 text-gray-300 hover:text-white text-sm transition">
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" />
                    Hall Admin Login
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Contact</h4>
              <div className="space-y-2.5">
                <a href="mailto:katti.shrinidhi@gmail.com" className="flex items-center gap-2.5 text-gray-300 hover:text-white text-sm transition">
                  <Mail className="w-4 h-4 text-purple-400" />
                  katti.shrinidhi@gmail.com
                </a>
                <a href="tel:+917760437800" className="flex items-center gap-2.5 text-gray-300 hover:text-white text-sm transition">
                  <Phone className="w-4 h-4 text-pink-400" />
                  +91 77604 37800
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-gray-500 text-sm">&copy; 2026 QuickVenue. All rights reserved.</p>
            <p className="text-gray-500 text-sm">
              Designed &amp; developed by <span className="text-purple-400 font-medium">Shrinidhi Katti</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
